import { RemoteParticipant } from 'livekit-client'
import { useRef } from 'react'
import { getAudioEngineInstance, IAudioEngineRoom } from '@/core/AudioEngine'
import { AudioInputStreamInfo } from '@/UserContext'
import { logger } from '@/services/Logger'
import { LiveKitMetadata } from '@/services/RoomContextMusePeer'

const log = logger.createChildLogger({ name: 'egress-renderer' })

type Stream = {
    userId: string
    streamId: string
    info: AudioInputStreamInfo
}

type Manifest = Map<string, Stream>

type ManifestDiff = {
    added: string[]
    removed: string[]
}

export default function () {
    const manifests = useRef<Manifest>(new Map())
    const aeRoom = useRef<IAudioEngineRoom>(getAudioEngineInstance().createRoom())

    const handleMetadata = (p: RemoteParticipant, metadata: LiveKitMetadata | null, leaving: boolean = false) => {
        if (metadata) {
            const manifest = convertMetadataToUserManifest(leaving ? null : metadata)
            applyUserManifest(manifests.current, metadata.userUrn, manifest, aeRoom.current)
        }
    }

    return { aeRoom, handleMetadata, manifests }
}

function applyUserManifest(fullManifest: Manifest, userId: string, userManifest: Manifest, room: IAudioEngineRoom) {
    const manifestDiff = diffUserManifest(fullManifest, userManifest, userId)
    log.verbose('CompositeTemplate', 'applying manifest diff', { userId, manifestDiff })

    for (const streamId of manifestDiff.added) {
        const stream = userManifest.get(streamId)!
        fullManifest.set(streamId, stream)
        room.receiveRemoteStream(streamId, stream.info)
    }
    for (const streamId of manifestDiff.removed) {
        fullManifest.delete(streamId)
        room.removeRemoteStream(streamId)
    }
}

function diffUserManifest(fullManifest: Manifest, userManifest: Manifest, userId: string): ManifestDiff {
    let added: string[] = []
    let removed: string[] = []

    for (const [streamId, stream] of fullManifest) {
        if (stream.userId === userId) {
            if (!userManifest.has(streamId)) {
                removed.push(streamId)
            }
        }
    }

    for (const [streamId, _stream] of userManifest) {
        if (!fullManifest.has(streamId)) {
            added.push(streamId)
        }
    }

    return {
        added,
        removed,
    }
}

function convertMetadataToUserManifest(metadata: LiveKitMetadata | null): Manifest {
    let userManifest: Manifest = new Map()

    for (const stream of metadata?.manifest?.localSfuAudios ?? []) {
        const streamId = stream.info.audioEngineStreamUuid
        if (streamId && stream.info.isAudioEngine) {
            userManifest.set(streamId, {
                userId: metadata?.userUrn ?? '',
                streamId: streamId,
                info: stream.info,
            })
        }
    }

    return userManifest
}
